import { useState, type CSSProperties } from "react";
import { useTheme } from "../lib/theme";

/**
 * /fonts — Orbitron speed-cut tuner. Chosen header face (Orbitron) in both
 * weights, under the live speed-cut treatment (Cannon / Orion / New World
 * style: horizontal slices + speedy skew), with tunable slice/gap/skew. Dial
 * it, then tell me the numbers to bake in. Throwaway route — delete when done.
 */

const FONTS = [
  { name: "Orbitron 900", family: '"Orbitron", sans-serif', weight: 900 },
  { name: "Orbitron 700", family: '"Orbitron", sans-serif', weight: 700 },
];

type CutOpts = {
  cut: boolean;
  solid: number;
  gap: number;
  skew: number;
  upper: boolean;
};

function SpeedText({
  text,
  family,
  weight,
  size,
  o,
}: {
  text: string;
  family: string;
  weight: number;
  size: string;
  o: CutOpts;
}) {
  const base: CSSProperties = {
    fontFamily: family,
    fontWeight: weight,
    fontSize: size,
    lineHeight: 1.15,
    color: "var(--amber)",
    display: "inline-block",
    textTransform: o.upper ? "uppercase" : "none",
    transform: `skewX(${o.skew}deg)`,
  };
  const mask = `repeating-linear-gradient(0deg, #000 0 ${o.solid}px, transparent ${o.solid}px ${o.solid + o.gap}px)`;
  const style: CSSProperties = o.cut
    ? {
        ...base,
        WebkitMaskImage: mask,
        maskImage: mask,
        filter:
          "drop-shadow(0 0 4px rgba(255,174,61,.75)) drop-shadow(0 0 12px rgba(255,174,61,.4))",
      }
    : { ...base, textShadow: "var(--text-glow-shadow)" };
  return <span style={style}>{text}</span>;
}

function Slider({
  label,
  value,
  min,
  max,
  onChange,
}: {
  label: string;
  value: number;
  min: number;
  max: number;
  onChange: (n: number) => void;
}) {
  return (
    <label className="flex items-center gap-3">
      <span className="vfd-box w-[136px] flex-none text-center">
        {label} {value}
      </span>
      <input
        type="range"
        min={min}
        max={max}
        value={value}
        onChange={(e) => onChange(Number(e.target.value))}
        className="w-40 accent-[var(--amber)]"
      />
    </label>
  );
}

export function FontLab() {
  const { theme, toggle } = useTheme();
  const [o, setO] = useState<CutOpts>({ cut: true, solid: 6, gap: 2, skew: -8, upper: true });
  const set = (patch: Partial<CutOpts>) => setO((p) => ({ ...p, ...patch }));

  return (
    <div className="min-h-dvh px-[clamp(20px,5vw,64px)] py-10">
      <div className="mx-auto max-w-4xl">
        <header className="mb-8 flex flex-wrap items-center justify-between gap-4">
          <div>
            <a href="/" className="etch-label !text-cyan">
              ← back to site
            </a>
            <h1 className="speedcut font-display mt-3 text-[clamp(20px,4vw,34px)] uppercase text-amber">
              Type Test
            </h1>
          </div>
          <button className="btn btn-primary" onClick={toggle}>
            {theme === "dark" ? "☀ light" : "☾ dark"}
          </button>
        </header>

        {/* Controls */}
        <div className="panel mb-8 flex flex-col gap-3 p-5">
          <div className="flex flex-wrap gap-4">
            <label className="flex items-center gap-2 font-body">
              <input
                type="checkbox"
                checked={o.cut}
                onChange={(e) => set({ cut: e.target.checked })}
                className="accent-[var(--amber)]"
              />
              speed-cut
            </label>
            <label className="flex items-center gap-2 font-body">
              <input
                type="checkbox"
                checked={o.upper}
                onChange={(e) => set({ upper: e.target.checked })}
                className="accent-[var(--amber)]"
              />
              UPPERCASE
            </label>
          </div>
          <Slider label="slice" value={o.solid} min={2} max={16} onChange={(n) => set({ solid: n })} />
          <Slider label="gap" value={o.gap} min={1} max={9} onChange={(n) => set({ gap: n })} />
          <Slider label="skew" value={o.skew} min={-24} max={0} onChange={(n) => set({ skew: n })} />
        </div>

        {/* Samples per font */}
        <div className="flex flex-col gap-6">
          {FONTS.map((f) => (
            <section key={f.name} className="screen p-6">
              <div className="vfd-box mb-5">{f.name}</div>
              <div className="flex flex-col gap-5 overflow-hidden">
                <SpeedText text="AJ" family={f.family} weight={f.weight} size="72px" o={o} />
                <SpeedText text="GRSTNHBR" family={f.family} weight={f.weight} size="44px" o={o} />
                <SpeedText text="the film lab" family={f.family} weight={f.weight} size="34px" o={o} />
                <SpeedText
                  text="TURBO PHOTO FILM LAB"
                  family={f.family}
                  weight={f.weight}
                  size="22px"
                  o={o}
                />
              </div>
            </section>
          ))}
        </div>

        <p className="mt-8 font-body text-text-mute">
          Pick a font + dial the slice/gap/skew you like, then tell me the name and the numbers.
        </p>
      </div>
    </div>
  );
}
